import { useEffect, useId, useRef, useState } from "react";
import { SharingApi } from "../api";
import { GrantData, TodoData } from "../../../types";
import { useFocusTrap } from "../useFocusTrap";

// Owner-side controls for one top-level project. Nothing is fetched until the
// owner clicks: Share opens a notice modal and creates one invite only on confirm; "Shared with" loads the grants
// (max 50) once, and Remove filters the list locally.
export const ShareControls = ({ project }: { project: TodoData }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantData[] | null>(null);
  const [showGrants, setShowGrants] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Mirrors `busy` so the Escape listener (bound once per open) sees the current value.
  const busyRef = useRef(false);

  const openModal = () => {
    setNote(null);
    setLink(null);
    setError(null);
    setCopyState("idle");
    setModalOpen(true);
  };

  // Ignored while an invite is being created, so its link can't be lost and a
  // second invite isn't created by reopening.
  const closeModal = () => {
    if (busyRef.current) return;
    clearTimeout(copyTimer.current);
    setModalOpen(false);
  };

  // Only runs on explicit confirm; the button is disabled while in flight.
  const confirmShare = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const { token } = await SharingApi.createInvite(project.id, project.createdAt);
      setLink(`${window.location.origin}${window.location.pathname}#/invite/${token}`);
    } catch {
      setError("Couldn't create an invite link. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(link);
      ok = true;
    } catch {
      // Clipboard unavailable (e.g. non-HTTPS): the link stays selectable below.
    }
    setCopyState(ok ? "copied" : "failed");
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopyState("idle"), 1500);
  };

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  useFocusTrap(dialogRef, modalOpen, closeModal);

  const toggleGrants = async () => {
    const next = !showGrants;
    setShowGrants(next);
    if (!next || grants !== null) return;
    try {
      setGrants((await SharingApi.listGrants(project.id)).grants);
    } catch {
      setNote("Couldn't load who this is shared with.");
      setShowGrants(false);
    }
  };

  const remove = async (g: GrantData) => {
    try {
      await SharingApi.removeShare(g.parentId, g.inviteeId);
      setGrants((list) => list?.filter((x) => x.inviteeId !== g.inviteeId) ?? null);
    } catch {
      setNote("Couldn't remove that share.");
    }
  };

  return (
    <div className="shrink-0 text-xs">
      <button onClick={openModal} className="min-h-10 sm:min-h-0 text-neutral-500 underline">
        Share
      </button>{" "}
      <button onClick={toggleGrants} className="min-h-10 sm:min-h-0 text-neutral-400">
        Shared with
      </button>
      {note && <p className="text-neutral-500">{note}</p>}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-safe-modal">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded bg-white p-4 text-sm shadow outline-none"
          >
            <h2 id={titleId} className="font-semibold">
              Share this list
            </h2>
            {!link ? (
              <>
                <p className="mt-2 text-neutral-600">
                  The person you share with will see your name and the items in this list, and can edit them and mark
                  them done.
                </p>
                {error && (
                  <p role="alert" className="mt-2 text-red-600">
                    {error}
                  </p>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={closeModal} disabled={busy} className="min-h-10 sm:min-h-0 px-2 py-1 text-neutral-500">
                    Cancel
                  </button>
                  <button onClick={confirmShare} disabled={busy} className="min-h-10 sm:min-h-0 rounded border px-3 sm:px-2 py-1">
                    Share
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-neutral-600">Link ready, valid 7 days, one use.</p>
                <input
                  readOnly
                  value={link}
                  aria-label="Invite link"
                  onFocus={(e) => e.currentTarget.select()}
                  className="mt-2 w-full min-w-0 border rounded px-1 py-0.5"
                />
                <p aria-live="polite" className="mt-1 h-4 text-xs text-neutral-500">
                  {copyState === "copied" && "Copied"}
                  {copyState === "failed" && "Copy failed"}
                </p>
                <div className="mt-2 flex justify-end gap-2">
                  <button onClick={copyLink} className="min-h-10 sm:min-h-0 rounded border px-3 sm:px-2 py-1">
                    Copy
                  </button>
                  <button onClick={closeModal} className="min-h-10 sm:min-h-0 px-2 py-1 text-neutral-500">
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showGrants && grants && (
        <ul className="mt-1 space-y-0.5">
          {grants.length === 0 && <li className="text-neutral-400">Not shared with anyone.</li>}
          {grants.map((g) => (
            <li key={g.inviteeId} className="flex justify-between gap-2">
              <span className="text-neutral-500">
                User …{g.inviteeId.slice(-6)} · {new Date(g.createdAt).toLocaleDateString()}
              </span>
              <button onClick={() => remove(g)} className="min-h-10 sm:min-h-0 text-neutral-400">
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
